import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Monitor } from 'lucide-react';
import { isMobileOrTabletClient } from '@/lib/deviceGate';

function MobileBlockedPresentation() {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-5 sm:p-8 pointer-events-auto"
      data-testid="mobile-blocked-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-block-title"
      aria-describedby="mobile-block-desc"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-md"
        aria-hidden
      />
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28, delay: 0.06 }}
        className="relative z-[201] w-full max-w-md rounded-3xl border border-cyan-400/35 bg-slate-950/75 px-6 py-8 sm:px-10 sm:py-10 shadow-[0_0_0_1px_rgba(34,211,238,0.12),0_25px_80px_-12px_rgba(0,0,0,0.65),0_0_60px_-15px_rgba(34,211,238,0.25)] text-center text-white"
      >
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-500/15 ring-1 ring-cyan-400/40">
          <Monitor className="h-7 w-7 text-cyan-300" aria-hidden />
        </div>
        <h1
          id="mobile-block-title"
          className="text-2xl sm:text-3xl font-bold tracking-tight text-glow"
        >
          Oups…
        </h1>
        <p
          id="mobile-block-desc"
          className="mt-4 text-base sm:text-lg leading-relaxed text-white/85"
        >
          Ce site ne peut pas être consulté sur un téléphone ou une tablette. Merci de te connecter à
          partir d&apos;un ordinateur.
        </p>
        <p className="mt-6 text-sm text-white/55">
          Tu peux utiliser l&apos;icône <span className="text-cyan-300/90">engrenage</span> en haut à
          droite pour accéder au panneau administrateur.
        </p>
      </motion.div>
    </div>
  );
}

/**
 * Sur mobile / tablette : message par-dessus l’accueil, sauf sur `/admin` (panel professeur).
 */
export default function MobileGuestGate() {
  const mobile = useMemo(() => isMobileOrTabletClient(), []);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = pathname.startsWith('/admin');
  const showGuestBlock = mobile && !isAdminRoute;

  useEffect(() => {
    if (!showGuestBlock) return;
    if (pathname !== '/') navigate('/', { replace: true });
  }, [showGuestBlock, pathname, navigate]);

  if (!showGuestBlock) return null;
  return <MobileBlockedPresentation />;
}
